import styled from "styled-components";

import { DATA_E2E_KEY, type IStyledAttributes } from "@ryde/shared";
import { blueGreyMui, greyMui, rydeColors, ScreenDepth } from "@ryde/theme";

import { MAP_CONTROL_STACK } from "../map.constants";

/**
 * Geoman anchored its toolbar topleft, stacked under the zoom control, as two Leaflet control bars: the
 * draw tool on its own, then the four that act on existing shapes. This continues MapLibre's own control
 * stack — its 10px inset, plus the zoom group's two 29px buttons, plus the 10px it leaves between one
 * group and the next, plus whatever the host added to the stack (`MAP_CONTROL_STACK`).
 */
export const MapLibrePolygonEditorToolbarStyled = styled.div.attrs<IStyledAttributes>(
  {
    [DATA_E2E_KEY]: "maplibre-polygon-editor-toolbar",
  },
)`
  top: calc(78px + var(${MAP_CONTROL_STACK.extraHeightVar}, 0px));
  /* @noflip keeps it on the physical left in RTL (Hebrew) too — stylis-plugin-rtl won't flip it. */
  left: 10px /* @noflip */;
  position: absolute;
  z-index: ${ScreenDepth.MAP_ATTRIBUTIONS};
  display: flex;
  flex-direction: column;
  gap: 10px;
  width: fit-content;

  .mapToolGroup {
    display: flex;
    flex-direction: column;
    border: 1px solid ${blueGreyMui._200};
    border-radius: 4px;
    background: ${rydeColors.white};
  }

  /*
   * One tool: its button, and the action row that opens beside it. They are siblings inside this box
   * rather than the row sitting inside the button — nesting them meant every action click bubbled to the
   * button and toggled the tool off, so Remove Last Vertex dropped a corner and then ended the trace.
   *
   * It is also what positions the row: the row is absolute against this box, which the button fills.
   */
  .mapTool {
    position: relative;
    display: flex;

    /* Hairlines between the tools of a group, and rounded ends — Leaflet's own control-bar shape. */
    & + .mapTool .mapToolBtn {
      border-top: 1px solid ${blueGreyMui._200};
    }

    &:first-child .mapToolBtn {
      border-radius: 3px 3px 0 0;
    }

    &:last-child .mapToolBtn {
      border-radius: 0 0 3px 3px;
    }

    &:only-child .mapToolBtn {
      border-radius: 3px;
    }
  }

  .mapToolBtn {
    appearance: none;
    font: inherit;
    cursor: pointer;
    box-sizing: border-box;
    width: 29px;
    height: 29px;
    padding: 5px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: ${greyMui._700};
    background: ${rydeColors.white};
    border: none;

    &:hover {
      background: ${greyMui._100};
    }

    /* Geoman marked the active tool by pressing the button in, not by recolouring it. */
    &.selected {
      box-shadow: inset 0 -1px 5px 2px rgba(81, 77, 77, 0.31);
    }
  }
`;

/**
 * The row of text actions geoman opened beside whichever tool is active — Finish, Remove Last Vertex and
 * Cancel while tracing, and just Finish for the tools that stay on until dismissed.
 *
 * Physical left/right, because the toolbar itself is pinned to the physical left in RTL as well.
 */
export const MapLibrePolygonEditorActionsStyled = styled.div.attrs<IStyledAttributes>(
  {
    [DATA_E2E_KEY]: "maplibre-polygon-editor-actions",
  },
)`
  position: absolute;
  top: 0;
  left: 100% /* @noflip */;
  z-index: 1;
  display: flex;
  white-space: nowrap;
  direction: ltr /* @noflip */;

  .mapToolAction {
    appearance: none;
    font: inherit;
    cursor: pointer;
    height: 29px;
    line-height: 29px;
    padding: 0 10px;
    font-size: 12px;
    color: ${rydeColors.white};
    background-color: ${greyMui._600};
    border: none;
    border-inline-end: 1px solid ${greyMui._200};
    user-select: none;

    &:last-child {
      border-inline-end: none;
      border-radius: 0 3px 3px 0 /* @noflip */;
    }

    &:hover {
      background-color: ${greyMui._700};
    }
  }
`;
